<template>
    <div class="languages-form">
        <!-- Informacje o zawodach -->
        <panel-competition-form-box class="mat16">
            <div class="w-full mab16">
                <p class="section-title">Informacje o zawodach</p>
            </div>

            <div class="w-full">
                <p class="field-label mab8">Opis zawodów</p>
                <div class="i18n-pair i18n-pair--fill">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border" v-html="formatHtml(baseCompetition.description)" />
                    </div>
                    <div class="i18n-col i18n-col--area">
                        <panel-input-area v-model="translationCompetition.description" :rows="12" />
                    </div>
                </div>
            </div>

            <div class="w-full mat24">
                <p class="field-label mab8">Ważny komunikat</p>
                <div class="i18n-pair i18n-pair--fill">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border" v-html="formatHtml(baseCompetition.remarks)" />
                    </div>
                    <div class="i18n-col i18n-col--area">
                        <panel-input-area v-model="translationCompetition.remarks" :rows="4" />
                    </div>
                </div>
            </div>

            <div class="w-full mat24">
                <p class="field-label mab8">Oficjalna strona zawodów</p>
                <div class="i18n-pair i18n-pair--center">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border">{{ baseCompetition.website || '—' }}</p>
                    </div>
                    <div class="i18n-col">
                        <panel-input-text v-model="translationCompetition.website" hint="http://" />
                    </div>
                </div>
            </div>

            <div v-if="hasCycle" class="w-full mat24">
                <p class="field-label mab8">Zawody cykliczne</p>
                <div class="i18n-pair i18n-pair--center">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border">{{ baseCycleName || '—' }}</p>
                    </div>
                    <div class="i18n-col">
                        <panel-input-text v-model="local.cycle.translation.name" />
                    </div>
                </div>
            </div>
        </panel-competition-form-box>

        <!-- Klasyfikacje i kategorie -->
        <panel-competition-form-box v-if="local.classifications && local.classifications.length" class="mat16">
            <div class="w-full mab16">
                <p class="section-title">Klasyfikacje i kategorie</p>
            </div>
            <div class="w-full mab8">
                <p class="field-label">Nazwa klasyfikacji</p>
            </div>

            <div
                v-for="(classification, cIdx) in local.classifications"
                :key="classification.id"
                class="w-full classification-block"
                :class="{ mat16: cIdx > 0 }"
            >
                <div class="i18n-pair i18n-pair--center">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--strong">
                            {{ (classification.base && classification.base.name) || '—' }}
                        </p>
                    </div>
                    <div class="i18n-col">
                        <panel-input-text v-model="classification.translation.name" />
                    </div>
                </div>

                <div
                    v-if="classification.specialCategories && classification.specialCategories.length"
                    class="categories-pair"
                >
                    <div class="i18n-col categories-col">
                        <p
                            v-for="category in classification.specialCategories"
                            :key="`base-${category.id}`"
                            class="i18n-text i18n-text--nested"
                        >
                            {{ (category.base && category.base.name) || '—' }}
                        </p>
                    </div>
                    <div class="i18n-col categories-col">
                        <div
                            v-for="category in classification.specialCategories"
                            :key="category.id"
                            class="category-input"
                        >
                            <panel-input-text v-model="category.translation.name" />
                        </div>
                    </div>
                </div>
            </div>
        </panel-competition-form-box>

        <!-- Pola dodatkowe -->
        <panel-competition-form-box v-if="local.inputs && local.inputs.length" class="mat16">
            <div class="w-full mab16">
                <p class="section-title">Pola dodatkowe formularza</p>
            </div>

            <div v-for="(input, iIdx) in local.inputs" :key="input.id" class="w-full" :class="{ mat24: iIdx > 0 }">
                <p class="field-label mab8">{{ inputTypeLabel(input.type) }}</p>

                <template v-if="input.type === 'fc'">
                    <div class="i18n-pair i18n-pair--fill">
                        <div class="i18n-col">
                            <p
                                class="i18n-text i18n-text--border"
                                v-html="formatHtml(input.base && input.base.description)"
                            />
                        </div>
                        <div class="i18n-col i18n-col--area">
                            <panel-input-area v-model="input.translation.description" :rows="3" />
                        </div>
                    </div>
                </template>

                <template v-else-if="input.type === 'fs' || input.type === 'fr'">
                    <div class="i18n-pair i18n-pair--center mab8">
                        <div class="i18n-col">
                            <p class="i18n-text i18n-text--strong i18n-text--border">
                                {{ (input.base && input.base.name) || '—' }}
                            </p>
                        </div>
                        <div class="i18n-col">
                            <panel-input-text v-model="input.translation.name" />
                        </div>
                    </div>
                    <div class="categories-pair">
                        <div class="i18n-col categories-col">
                            <p
                                v-for="(option, oIdx) in inputOptions(input)"
                                :key="`base-opt-${input.id}-${oIdx}`"
                                class="i18n-text i18n-text--nested"
                            >
                                {{ baseInputOption(input, oIdx) }}
                            </p>
                        </div>
                        <div class="i18n-col categories-col">
                            <div
                                v-for="(option, oIdx) in inputOptions(input)"
                                :key="`${input.id}-opt-${oIdx}`"
                                class="category-input"
                            >
                                <panel-input-text :value="option" @input="setOption(input, oIdx, $event)" />
                            </div>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="i18n-pair i18n-pair--center">
                        <div class="i18n-col">
                            <p class="i18n-text i18n-text--border">{{ (input.base && input.base.name) || '—' }}</p>
                        </div>
                        <div class="i18n-col">
                            <panel-input-text v-model="input.translation.name" />
                        </div>
                    </div>
                </template>
            </div>
        </panel-competition-form-box>

        <!-- Pliki dla uczestnika -->
        <panel-competition-form-box class="mat16">
            <div class="w-full mab16">
                <p class="section-title">Pliki dla uczestnika</p>
            </div>

            <div class="w-full">
                <p class="field-label mab8">Regulamin zawodów</p>
                <div class="i18n-pair i18n-pair--center">
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border">
                            <a
                                v-if="baseCompetition.statuteLink"
                                :href="baseCompetition.statuteLink"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {{ baseCompetition.statuteLink }}
                            </a>
                            <template v-else>—</template>
                        </p>
                    </div>
                    <div class="i18n-col">
                        <panel-input-text v-model="translationCompetition.statuteLink" hint="http://" />
                    </div>
                </div>
            </div>

            <div
                v-if="translationCompetition.statementContents && translationCompetition.statementContents.length"
                class="w-full mat24"
            >
                <p class="field-label mab8">Zgody uczestników</p>
                <p class="field-hint mab16">
                    Możesz korzystać ze znaczników html: &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;br&gt;.
                </p>
                <div
                    v-for="(content, sIdx) in translationCompetition.statementContents"
                    :key="`statement-${sIdx}`"
                    class="i18n-pair i18n-pair--fill mat8"
                >
                    <div class="i18n-col">
                        <p class="i18n-text i18n-text--border" v-html="formatHtml(baseStatementContent(sIdx))" />
                    </div>
                    <div class="i18n-col i18n-col--area">
                        <panel-input-area :value="content" :rows="6" @input="setStatementContent(sIdx, $event)" />
                    </div>
                </div>
            </div>
        </panel-competition-form-box>

        <!-- Potwierdzenie rejestracji -->
        <panel-competition-form-box class="mat16">
            <div class="w-full mab16">
                <p class="section-title">Potwierdzenie rejestracji</p>
            </div>
            <div class="w-full">
                <p class="field-label mab8">Treść e-maila z potwierdzeniem rejestracji</p>
                <p class="field-hint mab16">
                    imię [firstname], nazwisko [lastname], data startu [date], godzina startu [time]
                </p>
                <div class="i18n-pair i18n-pair--fill">
                    <div class="i18n-col">
                        <p
                            class="i18n-text i18n-text--border"
                            v-html="formatHtml(baseCompetition.registerMailContent)"
                        />
                    </div>
                    <div class="i18n-col i18n-col--area">
                        <panel-input-area v-model="translationCompetition.registerMailContent" :rows="10" />
                    </div>
                </div>
            </div>
        </panel-competition-form-box>
    </div>
</template>

<script>
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputText from '@/components/PanelInputText';
    import PanelInputArea from '@/components/PanelInputArea';

    const INPUT_TYPE_LABELS = {
        fi: 'Krótka odpowiedź',
        fj: 'Krótka odpowiedź',
        fl: 'Krótka odpowiedź',
        fh: 'Krótka odpowiedź',
        fd: 'Krótka odpowiedź',
        fs: 'Lista wyboru',
        fr: 'Wybór',
        fc: 'Potwierdzenie',
    };

    export default {
        name: 'PanelCompetitionLanguagesForm',
        components: {
            PanelCompetitionFormBox,
            PanelInputText,
            PanelInputArea,
        },
        props: {
            value: {
                type: Object,
                required: true,
            },
        },
        computed: {
            local: {
                get() {
                    return this.value;
                },
                set(val) {
                    this.$emit('input', val);
                },
            },
            baseCompetition() {
                return (this.local.competition && this.local.competition.base) || {};
            },
            translationCompetition() {
                return (this.local.competition && this.local.competition.translation) || {};
            },
            hasCycle() {
                return Boolean(this.local.cycle);
            },
            baseCycleName() {
                return (this.local.cycle && this.local.cycle.base && this.local.cycle.base.name) || '';
            },
        },
        methods: {
            formatHtml(text) {
                if (text == null || text === '') {
                    return '—';
                }
                const str = String(text);
                if (str.includes('<br') || str.includes('<p') || str.includes('<div')) {
                    return str;
                }
                return str.replace(/\n/g, '<br>');
            },
            inputTypeLabel(type) {
                return INPUT_TYPE_LABELS[type] || 'Pole formularza';
            },
            inputOptions(input) {
                return (input.translation && input.translation.options) || [];
            },
            baseInputOption(input, index) {
                const options = (input.base && input.base.options) || [];
                return options[index] != null ? options[index] : '—';
            },
            baseStatementContent(index) {
                const list = this.baseCompetition.statementContents || [];
                return list[index] != null ? list[index] : '';
            },
            setOption(input, index, value) {
                if (!input.translation.options) {
                    this.$set(input.translation, 'options', []);
                }
                this.$set(input.translation.options, index, value);
            },
            setStatementContent(index, value) {
                this.$set(this.translationCompetition.statementContents, index, value);
            },
        },
    };
</script>

<style scoped>
    .section-title {
        font-size: 16px;
        font-weight: 600;
        margin: 0;
    }

    .field-label {
        font-size: 15px;
        font-weight: 500;
        display: block;
        margin-bottom: 16px;
    }

    .field-hint {
        font-size: 12px;
        line-height: 1.67;
        margin: 0 0 16px 0;
    }

    .w-full {
        width: 100%;
    }

    /* Two fixed columns: PL | translation (legacy-like) */
    .i18n-pair,
    .categories-pair {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        width: 100%;
        align-items: start;
    }

    .i18n-pair--center {
        align-items: center;
    }

    .i18n-pair--fill {
        align-items: stretch;
    }

    .i18n-col {
        min-width: 0;
    }

    .i18n-col--area {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .i18n-col--area >>> .textarea-field {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 100%;
    }

    .i18n-col--area >>> .input-field__textarea {
        flex: 1 1 auto;
        width: 100%;
        height: 100%;
        min-height: 100%;
        box-sizing: border-box;
        resize: vertical;
    }

    .i18n-pair--fill .i18n-text--border {
        height: 100%;
        box-sizing: border-box;
    }

    .i18n-text {
        margin: 0;
        padding: 0;
        font-size: 14px;
        line-height: 1.5;
        word-break: break-word;
    }

    .i18n-text--border {
        padding: 8px 16px;
        border: 1px solid #fff;
    }

    .i18n-text--strong {
        font-weight: 600;
    }

    .i18n-text--nested {
        padding-left: 16px;
        margin: 8px 0;
        border-left: 2px solid #90a4ae;
        min-height: 40px;
        display: flex;
        align-items: center;
    }

    .i18n-text a {
        color: var(--color-dark-pastel-green, #50ae55);
        word-break: break-all;
    }

    .categories-pair {
        margin-top: 8px;
    }

    .categories-col {
        display: flex;
        flex-direction: column;
    }

    .category-input {
        margin: 8px 0;
        min-height: 40px;
    }

    .classification-block + .classification-block {
        border-top: 1px solid rgba(255, 255, 255, 0.12);
        padding-top: 16px;
    }

    @media (max-width: 991px) {
        .i18n-pair,
        .categories-pair {
            grid-template-columns: 1fr;
            gap: 12px;
        }

        .i18n-text--nested {
            min-height: 0;
        }
    }
</style>
